import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { eventApi, IEvent } from "../services/api/eventApi";
import { churchApi, Church } from "../services/api/churchApi";
import { olympicApi } from "../services/api/olympicApi";
import {
  Gender,
  OLYMPIC_PRICE,
  OLYMPIC_REGIONS,
  OLYMPIC_TEAM_META,
  OlympicBasicInfo,
  OlympicCoach,
  OlympicMealCount,
  OlympicPlayer,
  OlympicRegion,
  OlympicTeam,
  OlympicTeamType,
  calculateOlympicCosts,
  canopyMaxCount,
  countQualifiedTeams,
  gradeOptionsFor,
} from "../types/olympic";

const formatPhone = (value: string) => {
  const n = (value || "").replace(/[^\d]/g, "");
  if (n.length <= 3) return n;
  if (n.length <= 7) return `${n.slice(0, 3)}-${n.slice(3)}`;
  return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7, 11)}`;
};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const labelForIndex = (index: number): string => {
  // A=1, B=2, ...
  return String.fromCharCode(64 + index) + "팀";
};

function buildBlankCoaches(): OlympicCoach[] {
  return [
    { name: "", contact: "" },
    { name: "", contact: "" },
  ];
}

function buildBlankPlayers(teamType: OlympicTeamType): OlympicPlayer[] {
  const players: OlympicPlayer[] = [];
  for (let i = 1; i <= 10; i++) {
    const player: OlympicPlayer = { index: i, name: "", grade: "", isSubstitute: false };
    if (teamType === "TNT_MIXED" && i <= 5) {
      player.gender = "F";
    } else if (teamType === "TNT_MIXED") {
      player.gender = undefined;
    }
    players.push(player);
  }
  for (let i = 11; i <= 12; i++) {
    const sub: OlympicPlayer = { index: i, name: "", grade: "", isSubstitute: true };
    if (teamType === "TNT_MIXED") sub.gender = undefined;
    players.push(sub);
  }
  return players;
}

function buildBlankTeam(teamType: OlympicTeamType, teamLabel: string): OlympicTeam {
  return {
    id: uid(),
    teamType,
    teamLabel,
    coaches: buildBlankCoaches(),
    players: buildBlankPlayers(teamType),
  };
}

const OlympicRegisterPage: React.FC = () => {
  const { eventId, churchId } = useParams<{ eventId: string; churchId?: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [eventInfo, setEventInfo] = useState<IEvent | null>(null);

  const [churches, setChurches] = useState<Church[]>([]);
  const [churchSearchValue, setChurchSearchValue] = useState<string>("");
  const [churchSearchLoading, setChurchSearchLoading] = useState(false);
  const [selectedChurchId, setSelectedChurchId] = useState<string | null>(churchId || null);

  const [basicInfo, setBasicInfo] = useState<OlympicBasicInfo>({
    region: "",
    church_number: "",
    church_name: "",
    receptionManager: { name: "", contact: "" },
    sparksSupervisor: { name: "", contact: "" },
    tntSupervisor: { name: "", contact: "" },
    pastor: { name: "", contact: "" },
  });

  const [teams, setTeams] = useState<OlympicTeam[]>([
    buildBlankTeam("SPARKS", "A팀"),
    buildBlankTeam("TNT_F", "A팀"),
    buildBlankTeam("TNT_M", "A팀"),
    buildBlankTeam("TNT_MIXED", "혼성팀"),
  ]);

  const [observerCount, setObserverCount] = useState<number>(0);
  const [mealCount, setMealCount] = useState<OlympicMealCount>({ elem: 0, midHigh: 0, general: 0 });
  const [canopyCount, setCanopyCount] = useState<number>(0);

  // ---- Event 조회
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;
      try {
        const events = await eventApi.getEvents();
        const found = events.find((e) => e._id === eventId);
        if (found) setEventInfo(found);
      } catch (err: any) {
        console.error("Error fetching event:", err);
      }
    };
    fetchEvent();
  }, [eventId]);

  // ---- Church 검색 (Autocomplete)
  useEffect(() => {
    const run = async () => {
      if (!churchSearchValue || churchSearchValue.length < 1) return;
      setChurchSearchLoading(true);
      try {
        const res = await churchApi.searchChurches({ search: churchSearchValue, limit: 20 });
        if (res.success) setChurches(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setChurchSearchLoading(false);
      }
    };
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [churchSearchValue]);

  // ---- 초기 Church 로드 (URL churchId가 있을 때)
  useEffect(() => {
    const run = async () => {
      if (!churchId) return;
      try {
        const [mainId, subId] = churchId.split("-");
        const res = await churchApi.getChurchById(mainId, subId);
        if (res.success && res.data) {
          setBasicInfo((prev) => ({
            ...prev,
            church_number: mainId,
            church_name: res.data.name,
          }));
          setSelectedChurchId(churchId);
        }
      } catch (err) {
        console.error(err);
      }
    };
    run();
  }, [churchId]);

  // ---- 기존 등록 데이터 로드
  useEffect(() => {
    const run = async () => {
      const cid = churchId || selectedChurchId;
      if (!eventId || !cid) return;
      setLoading(true);
      try {
        const existing = await olympicApi.getRegistration(eventId, cid);
        if (existing) {
          setBasicInfo(existing.basicInfo);
          setTeams(existing.teams && existing.teams.length > 0 ? existing.teams : teams);
          setObserverCount(existing.observerCount || 0);
          setMealCount(existing.mealCount || { elem: 0, midHigh: 0, general: 0 });
          setCanopyCount(existing.canopyCount || 0);
        }
      } catch (err: any) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, churchId, selectedChurchId]);

  // ---- 파생값
  const costs = useMemo(
    () => calculateOlympicCosts({ basicInfo, teams, observerCount, mealCount, canopyCount }),
    [basicInfo, teams, observerCount, mealCount, canopyCount]
  );
  const region = basicInfo.region;
  const showMealBlock = region === "CHUNGJEON";
  const showCanopyBlock = region === "SUDO" || region === "GYEONGSANG";
  const maxCanopy = canopyMaxCount(teams.length);
  const qualifiedTeamCount = useMemo(() => countQualifiedTeams(teams), [teams]);
  const totalPlayers = qualifiedTeamCount * 10;
  const totalCoaches = qualifiedTeamCount * 2;

  // ---- 팀 조작 헬퍼
  const updateTeam = (teamId: string, updater: (t: OlympicTeam) => OlympicTeam) => {
    setTeams((prev) => prev.map((t) => (t.id === teamId ? updater(t) : t)));
  };
  const removeTeam = (teamId: string) => {
    setTeams((prev) => prev.filter((t) => t.id !== teamId));
  };
  const addTeam = (teamType: OlympicTeamType) => {
    if (!OLYMPIC_TEAM_META[teamType].allowMultiple) return;
    const sameType = teams.filter((t) => t.teamType === teamType);
    const nextLabel = labelForIndex(sameType.length + 1);
    setTeams((prev) => [...prev, buildBlankTeam(teamType, nextLabel)]);
  };

  const updateCoach = (teamId: string, coachIdx: number, patch: Partial<OlympicCoach>) => {
    updateTeam(teamId, (t) => ({
      ...t,
      coaches: t.coaches.map((c, i) => (i === coachIdx ? { ...c, ...patch } : c)),
    }));
  };

  const updatePlayer = (teamId: string, playerIdx: number, patch: Partial<OlympicPlayer>) => {
    updateTeam(teamId, (t) => ({
      ...t,
      players: t.players.map((p, i) => (i === playerIdx ? { ...p, ...patch } : p)),
    }));
  };

  // ---- 유효성 검증
  const validate = (): string | null => {
    if (!region) return "참가지역을 선택해주세요.";
    if (!basicInfo.church_number || !basicInfo.church_name) return "교회를 선택해주세요.";
    if (!basicInfo.receptionManager.name || !basicInfo.receptionManager.contact)
      return "접수담당자 이름/연락처를 입력해주세요.";
    if (teams.length === 0) return "최소 한 팀 이상 입력해주세요.";

    const mixed = teams.filter((t) => t.teamType === "TNT_MIXED");
    if (mixed.length > 1) return "T&T 혼성팀은 한 팀만 등록할 수 있습니다.";
    for (const mt of mixed) {
      for (let i = 0; i < 5; i++) {
        const p = mt.players[i];
        if (p && p.name && p.gender !== "F") {
          return "T&T 혼성팀 선수 1~5번은 반드시 여자여야 합니다.";
        }
      }
    }

    // 최소 선수 1명 이상 있는지
    const anyPlayer = teams.some((t) => t.players.some((p) => p.name.trim()));
    if (!anyPlayer) return "최소 한 명 이상의 선수를 입력해주세요.";

    if (showCanopyBlock && canopyCount > maxCanopy) {
      return `현재 팀 수(${teams.length}팀) 기준 캐노피는 최대 ${maxCanopy}동까지 신청 가능합니다.`;
    }

    return null;
  };

  const handleSubmit = async () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!eventId) return;

    const cid = selectedChurchId || `${basicInfo.church_number}-a`;
    setSubmitting(true);
    try {
      const res = await olympicApi.register(eventId, cid, {
        basicInfo,
        teams,
        observerCount,
        mealCount,
        canopyCount,
        costs,
      });
      if (res.success) {
        setSuccessOpen(true);
      } else {
        setError(res.message || "등록에 실패했습니다.");
      }
    } catch (err: any) {
      setError(err.message || "등록에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const teamGroups = useMemo(() => {
    const group: Record<OlympicTeamType, OlympicTeam[]> = {
      SPARKS: [],
      TNT_F: [],
      TNT_M: [],
      TNT_MIXED: [],
    };
    teams.forEach((t) => group[t.teamType].push(t));
    return group;
  }, [teams]);

  // ---------- Render ----------

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {eventInfo?.event_Name || "올림픽"} 참가 신청
          </Typography>
          <Typography variant="body2" color="text.secondary">
            2026 Awana Olympics 참가 선수 명단 입력
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate("/")}>메인으로</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={18} sx={{ mr: 1 }} />}

      {/* 기본 정보 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>기본 정보</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>참가지역</InputLabel>
              <Select
                label="참가지역"
                value={region}
                onChange={(e) => setBasicInfo({ ...basicInfo, region: e.target.value as OlympicRegion })}
              >
                {OLYMPIC_REGIONS.map((r) => (
                  <MenuItem key={r.code} value={r.code}>
                    {r.label} — {r.date} ({r.place})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={churches}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : `${opt.mainId} · ${opt.name}`
              }
              loading={churchSearchLoading}
              onInputChange={(_, v) => setChurchSearchValue(v)}
              onChange={(_, v) => {
                if (v && typeof v !== "string") {
                  setBasicInfo({
                    ...basicInfo,
                    church_number: v.mainId,
                    church_name: v.name,
                  });
                  setSelectedChurchId(`${v.mainId}-${v.subId}`);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="등록번호 / 교회 검색" required />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="등록번호"
              value={basicInfo.church_number}
              InputProps={{ readOnly: true }}
              helperText="교회 검색으로 자동 입력됩니다"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="교회 이름"
              value={basicInfo.church_name}
              InputProps={{ readOnly: true }}
              helperText="교회 검색으로 자동 입력됩니다"
            />
          </Grid>

          {(["receptionManager", "sparksSupervisor", "tntSupervisor", "pastor"] as const).map((key) => {
            const labelMap = {
              receptionManager: "접수 담당자",
              sparksSupervisor: "Sparks 감독관",
              tntSupervisor: "T&T 감독관",
              pastor: "담임 사역자",
            } as const;
            return (
              <React.Fragment key={key}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={`${labelMap[key]} 이름`}
                    value={basicInfo[key].name}
                    onChange={(e) =>
                      setBasicInfo({ ...basicInfo, [key]: { ...basicInfo[key], name: e.target.value } })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={`${labelMap[key]} 연락처`}
                    value={basicInfo[key].contact}
                    onChange={(e) =>
                      setBasicInfo({
                        ...basicInfo,
                        [key]: { ...basicInfo[key], contact: formatPhone(e.target.value) },
                      })
                    }
                  />
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      </Paper>

      {/* 팀 그룹 4개 */}
      {(["SPARKS", "TNT_F", "TNT_M", "TNT_MIXED"] as OlympicTeamType[]).map((teamType) => (
        <TeamGroup
          key={teamType}
          teamType={teamType}
          teams={teamGroups[teamType]}
          basicInfo={basicInfo}
          onAdd={() => addTeam(teamType)}
          onRemove={removeTeam}
          onCoachChange={updateCoach}
          onPlayerChange={updatePlayer}
        />
      ))}

      {/* 참관자 / 식사 / 캐노피 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>참관자 / 식사 / 캐노피</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label={`참관자 수 (${OLYMPIC_PRICE.OBSERVER.toLocaleString()}원/인)`}
              value={observerCount}
              inputProps={{ min: 0 }}
              onChange={(e) => setObserverCount(Math.max(0, Number(e.target.value) || 0))}
            />
          </Grid>

          {showMealBlock && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  식당 식사 — 충청 전라지역만 제공
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={`초등학생 (${OLYMPIC_PRICE.MEAL_ELEM.toLocaleString()}원)`}
                  value={mealCount.elem}
                  inputProps={{ min: 0 }}
                  onChange={(e) => setMealCount({ ...mealCount, elem: Math.max(0, Number(e.target.value) || 0) })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={`중·고등학생 (${OLYMPIC_PRICE.MEAL_MID_HIGH.toLocaleString()}원)`}
                  value={mealCount.midHigh}
                  inputProps={{ min: 0 }}
                  onChange={(e) =>
                    setMealCount({ ...mealCount, midHigh: Math.max(0, Number(e.target.value) || 0) })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={`일반 (${OLYMPIC_PRICE.MEAL_GENERAL.toLocaleString()}원)`}
                  value={mealCount.general}
                  inputProps={{ min: 0 }}
                  onChange={(e) =>
                    setMealCount({ ...mealCount, general: Math.max(0, Number(e.target.value) || 0) })
                  }
                />
              </Grid>
            </>
          )}

          {showCanopyBlock && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  캐노피 — 수도권·경상지역만 신청 (3팀까지 최대 1동, 4팀 이상 최대 2동)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Tooltip title={teams.length < 1 ? "팀을 먼저 추가해 주세요" : ""}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`캐노피 수 (${OLYMPIC_PRICE.CANOPY.toLocaleString()}원/동)`}
                    value={canopyCount}
                    disabled={teams.length < 1}
                    inputProps={{ min: 0, max: maxCanopy }}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(maxCanopy, Number(e.target.value) || 0));
                      setCanopyCount(v);
                    }}
                  />
                </Tooltip>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* 금액 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>금액 요약</Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography>
              팀 참가비 ({qualifiedTeamCount}팀 · 선수 {totalPlayers}명 · 코치 {totalCoaches}명
              {qualifiedTeamCount !== teams.length ? ` / 입력중 ${teams.length}팀` : ""})
            </Typography>
            {qualifiedTeamCount !== teams.length && (
              <Typography variant="caption" color="text.secondary">
                정규선수 10명을 모두 입력한 팀만 참가비에 반영됩니다.
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography align="right">{costs.teamFee.toLocaleString()}원</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography>참관자 ({observerCount}명)</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography align="right">{costs.observers.toLocaleString()}원</Typography>
          </Grid>

          {showMealBlock && (
            <>
              <Grid item xs={6}>
                <Typography>
                  식당 식사 (초등 {mealCount.elem}명 / 중고등 {mealCount.midHigh}명 / 일반 {mealCount.general}명)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right">{costs.meals.toLocaleString()}원</Typography>
              </Grid>
            </>
          )}

          {showCanopyBlock && (
            <>
              <Grid item xs={6}>
                <Typography>캐노피 ({canopyCount}동)</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right">{costs.canopies.toLocaleString()}원</Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

          <Grid item xs={6}>
            <Typography variant="h6">합계</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" align="right" color="primary">
              {costs.total.toLocaleString()}원
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate("/")}>취소</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "저장 중..." : "접수 제출"}
        </Button>
      </Box>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        PaperProps={{ sx: { minWidth: 360 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon color="success" />
          접수가 완료되었습니다
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            {basicInfo.church_name} 교회의 올림픽 참가 신청이 저장되었습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            합계 금액: <strong>{costs.total.toLocaleString()}원</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            수정이 필요하면 같은 링크에서 다시 접속해 내용을 갱신할 수 있습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessOpen(false)}>이 페이지 유지</Button>
          <Button variant="contained" onClick={() => navigate("/")}>메인으로</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// -------------------- TeamGroup --------------------

interface TeamGroupProps {
  teamType: OlympicTeamType;
  teams: OlympicTeam[];
  basicInfo: OlympicBasicInfo;
  onAdd: () => void;
  onRemove: (teamId: string) => void;
  onCoachChange: (teamId: string, coachIdx: number, patch: Partial<OlympicCoach>) => void;
  onPlayerChange: (teamId: string, playerIdx: number, patch: Partial<OlympicPlayer>) => void;
}

const TeamGroup: React.FC<TeamGroupProps> = ({
  teamType,
  teams,
  basicInfo,
  onAdd,
  onRemove,
  onCoachChange,
  onPlayerChange,
}) => {
  const meta = OLYMPIC_TEAM_META[teamType];

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">{meta.label} 팀</Typography>
        {meta.allowMultiple && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
            팀 추가
          </Button>
        )}
      </Box>

      {teams.length === 0 && (
        <Alert severity="info">아직 등록된 {meta.label} 팀이 없습니다.</Alert>
      )}

      {teams.map((team) => (
        <TeamCard
          key={team.id}
          team={team}
          basicInfo={basicInfo}
          canRemove={meta.allowMultiple && teams.length > 1}
          onRemove={() => onRemove(team.id)}
          onCoachChange={(idx, patch) => onCoachChange(team.id, idx, patch)}
          onPlayerChange={(idx, patch) => onPlayerChange(team.id, idx, patch)}
        />
      ))}
    </Paper>
  );
};

// -------------------- TeamCard --------------------

interface TeamCardProps {
  team: OlympicTeam;
  basicInfo: OlympicBasicInfo;
  canRemove: boolean;
  onRemove: () => void;
  onCoachChange: (coachIdx: number, patch: Partial<OlympicCoach>) => void;
  onPlayerChange: (playerIdx: number, patch: Partial<OlympicPlayer>) => void;
}

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  basicInfo,
  canRemove,
  onRemove,
  onCoachChange,
  onPlayerChange,
}) => {
  const meta = OLYMPIC_TEAM_META[team.teamType];

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {meta.label} · {team.teamLabel}
          </Typography>
          {canRemove && (
            <IconButton color="error" onClick={onRemove} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="등록번호" value={basicInfo.church_number} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="교회 이름" value={basicInfo.church_name} InputProps={{ readOnly: true }} />
          </Grid>

          {team.coaches.map((c, i) => (
            <React.Fragment key={`c-${i}`}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small"
                  label={`코치 #${i + 1} 이름`}
                  value={c.name}
                  onChange={(e) => onCoachChange(i, { name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small"
                  label={`코치 #${i + 1} 연락처`}
                  value={c.contact}
                  onChange={(e) => onCoachChange(i, { contact: formatPhone(e.target.value) })}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>선수 명단 (1~10) / 교체 (11~12)</Typography>

        <Grid container spacing={1}>
          {team.players.map((p, i) => {
            const isMixed = team.teamType === "TNT_MIXED";
            const genderLocked = isMixed && !p.isSubstitute && p.index <= 5;
            const showGender = isMixed;

            return (
              <Grid item xs={12} key={`p-${i}`}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={2} md={1}>
                    <Typography variant="body2">
                      {p.isSubstitute ? `교체 #${p.index - 10}` : `선수 #${p.index}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={10} md={showGender ? 4 : 6}>
                    <TextField
                      fullWidth size="small"
                      label="이름"
                      value={p.name}
                      onChange={(e) => onPlayerChange(i, { name: e.target.value })}
                    />
                  </Grid>
                  {showGender && (
                    <Grid item xs={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>성별</InputLabel>
                        <Select
                          label="성별"
                          value={p.gender || ""}
                          disabled={genderLocked}
                          onChange={(e) => onPlayerChange(i, { gender: (e.target.value || undefined) as Gender | undefined })}
                        >
                          {genderLocked ? (
                            <MenuItem value="F">여 (고정)</MenuItem>
                          ) : (
                            [
                              <MenuItem key="none" value="">-</MenuItem>,
                              <MenuItem key="m" value="M">남</MenuItem>,
                              <MenuItem key="f" value="F">여</MenuItem>,
                            ]
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={showGender ? 6 : 12} md={showGender ? 4 : 5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>학년</InputLabel>
                      <Select
                        label="학년"
                        value={p.grade || ""}
                        onChange={(e) => onPlayerChange(i, { grade: (e.target.value || "") as string })}
                      >
                        <MenuItem value="">-</MenuItem>
                        {gradeOptionsFor(team.teamType).map((g) => (
                          <MenuItem key={g} value={g}>{g}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default OlympicRegisterPage;
